import { useEffect, useRef } from "react";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuthState } from "@/hooks/useAuthState";
import {
  PENDING_DIAGNOSIS_KEY,
  GUEST_DIAGNOSIS_HISTORY_KEY,
  type DiagnosisInput,
  type DiagnosisResult,
} from "@/lib/location-diagnosis";

const CLAIM_PARAM = "claim_loc_session";

/** Broadcast so dashboard tabs (e.g. "Mis Locales") can refetch immediately. */
export const SAVED_LOCATIONS_RESTORED_EVENT = "saved-locations-restored";

const fingerprintAddress = (
  formatted: string,
  lat: number | null,
  lng: number | null,
) => {
  const base = (formatted || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (lat != null && lng != null) return `${base}|${lat.toFixed(5)},${lng.toFixed(5)}`;
  return base;
};

const extractStreet = (formatted: string) =>
  (formatted || "").split(",")[0]?.trim() ?? (formatted || "");

const buildPayloadFromGuest = (parsed: any, userId: string) => {
  const inp = parsed.input as DiagnosisInput;
  const res = parsed.result as DiagnosisResult;
  const formattedAddress: string = parsed.formattedAddress || "";
  const lat: number | null = parsed.lat ?? null;
  const lng: number | null = parsed.lng ?? null;
  return {
    user_id: userId,
    place_id: null as string | null,
    address_fingerprint: fingerprintAddress(formattedAddress, lat, lng),
    formatted_address: formattedAddress,
    street_name: extractStreet(formattedAddress),
    city: "",
    zip_code: inp.zipCode,
    lat,
    lng,
    role: inp.role,
    business_sector: inp.sector,
    detailed_sector_id: parsed.detailedSectorId ?? (inp as any).detailedSectorId ?? "unknown",
    requested_rent: inp.requestedRent,
    sqm: inp.sqm,
    average_ticket: inp.role === "inquilino" ? inp.averageTicket : null,
    dias_apertura: inp.diasApertura,
    semaforo_result: res.semaforo,
    precio_m2_usuario: res.precioM2Usuario,
    precio_m2_zona: res.precioM2Zona,
    facturacion_limite: res.facturacionLimite,
    facturacion_optima: res.facturacionOptima,
    unidades_limite: res.unidadesLimite,
    unidades_optimas: res.unidadesOptimas,
    tenant_legal_form:
      inp.role === "propietario" ? parsed.tenantLegalForm || null : null,
    tenant_experience:
      inp.role === "propietario" ? parsed.tenantExperience || null : null,
    raw_input: {
      ...inp,
      candidates: parsed.candidates,
      tenantLegalForm: parsed.tenantLegalForm || null,
      tenantExperience: parsed.tenantExperience || null,
    } as any,
  } as any;
};

/** Rebuild a saved_locations payload from an evaluation_leads row claimed for this user. */
const buildPayloadFromLead = (lead: any, userId: string) => {
  const raw = (lead.raw_input ?? {}) as any;
  const formattedAddress: string =
    raw.formattedAddress || raw.manualAddress || raw.address || "";
  const lat: number | null = typeof raw.lat === "number" ? raw.lat : null;
  const lng: number | null = typeof raw.lng === "number" ? raw.lng : null;
  return {
    user_id: userId,
    place_id: null as string | null,
    address_fingerprint: fingerprintAddress(
      formattedAddress || `lead-${lead.id}`,
      lat,
      lng,
    ),
    formatted_address: formattedAddress,
    street_name: extractStreet(formattedAddress),
    city: "",
    zip_code: lead.zip_code,
    lat,
    lng,
    role: lead.role,
    business_sector: lead.business_sector,
    detailed_sector_id: raw.detailedSectorId ?? "unknown",
    requested_rent: Number(lead.requested_rent),
    sqm: Number(lead.sqm),
    average_ticket: lead.average_ticket != null ? Number(lead.average_ticket) : null,
    dias_apertura: lead.dias_apertura,
    semaforo_result: lead.semaforo_result,
    precio_m2_usuario: lead.precio_m2_usuario != null ? Number(lead.precio_m2_usuario) : null,
    precio_m2_zona: lead.precio_m2_zona != null ? Number(lead.precio_m2_zona) : null,
    facturacion_limite: lead.facturacion_minima != null ? Number(lead.facturacion_minima) : null,
    facturacion_optima: null,
    unidades_limite: lead.ventas_diarias_necesarias != null ? Number(lead.ventas_diarias_necesarias) : null,
    unidades_optimas: null,
    tenant_legal_form: lead.tenant_legal_form ?? null,
    tenant_experience: lead.tenant_experience ?? null,
    raw_input: raw,
  } as any;
};

const upsertSavedLocation = async (payload: any) => {
  // Partial unique index (place_id IS NULL) — onConflict by columns matches it.
  try {
    const supabase = await getSupabase();
    await supabase
      .from("saved_locations")
      .upsert(payload, { onConflict: "user_id,address_fingerprint" })
      .select("id")
      .maybeSingle();
  } catch (err) {
    // Fallback: ignore duplicates silently.
    console.warn("[useRestorePendingDiagnosis] upsert failed", err);
  }
};

const stripClaimParam = () => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(CLAIM_PARAM)) {
      url.searchParams.delete(CLAIM_PARAM);
      window.history.replaceState({}, "", url.toString());
    }
  } catch {/* ignore */}
};

/**
 * Global post-auth restore of any guest diagnosis.
 *
 * Restores from three sources, in order:
 *  1. URL `?claim_loc_session=<sid>` (cross-host OAuth handoff)
 *  2. `PENDING_DIAGNOSIS_KEY` (latest single guest diagnosis on this host)
 *  3. `GUEST_DIAGNOSIS_HISTORY_KEY` (up to 10 prior guest diagnoses)
 *
 * For every collected session id, claims anonymous `evaluation_leads` for the
 * current user and imports any that aren't already in `saved_locations`.
 * Then upserts local guest entries. Emits a window event so the dashboard's
 * "Mis Locales" tab refetches without needing a manual reload.
 */
export function useRestorePendingDiagnosis(): void {
  const { isAuthenticated, user } = useAuthState();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      const supabase = await getSupabase();
      let imported = 0;
      try {
        // ── Collect session ids from URL + localStorage ──
        const sessions = new Set<string>();
        let urlSession: string | null = null;
        if (typeof window !== "undefined") {
          try {
            urlSession = new URL(window.location.href).searchParams.get(CLAIM_PARAM);
            if (urlSession) sessions.add(urlSession);
          } catch {/* ignore */}
        }

        let latest: any = null;
        let history: any[] = [];
        try {
          const rawLatest = localStorage.getItem(PENDING_DIAGNOSIS_KEY);
          if (rawLatest) latest = JSON.parse(rawLatest);
        } catch {/* ignore */}
        try {
          const rawHist = localStorage.getItem(GUEST_DIAGNOSIS_HISTORY_KEY);
          if (rawHist) {
            const parsed = JSON.parse(rawHist);
            if (Array.isArray(parsed)) history = parsed;
          }
        } catch {/* ignore */}

        const localEntries: any[] = [...history];
        if (latest && !localEntries.some((e) => e?.savedAt === latest.savedAt)) {
          localEntries.push(latest);
        }
        for (const e of localEntries) {
          if (e?.sessionId) sessions.add(e.sessionId);
        }

        // ── Claim anonymous leads for every known session ──
        for (const sid of sessions) {
          try {
            await supabase.rpc("claim_evaluation_leads_by_session", {
              p_session_id: sid,
            });
          } catch {/* ignore */}
        }

        // ── Import any of the user's recent evaluation_leads that are not yet
        // in saved_locations. We don't restrict by session_id anymore: leads
        // already linked to this user (whether via claim RPC, server-side
        // user_id at insert time, or backfill) should always surface in
        // "Mis Locales". This also covers the case where the user was already
        // logged in when running the public diagnosis.
        try {
          const since = new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString();
          const { data: leads } = await supabase
            .from("evaluation_leads")
            .select("*")
            .eq("user_id", user.id)
            .gte("created_at", since)
            .order("created_at", { ascending: true })
            .limit(100);

          if (leads && leads.length > 0) {
            const { data: existing } = await supabase
              .from("saved_locations")
              .select("address_fingerprint")
              .eq("user_id", user.id);
            const have = new Set(
              (existing ?? []).map((r: any) => r.address_fingerprint),
            );
            for (const lead of leads) {
              const payload = buildPayloadFromLead(lead, user.id);
              if (have.has(payload.address_fingerprint)) continue;
              await upsertSavedLocation(payload);
              have.add(payload.address_fingerprint);
              imported += 1;
            }
          }
        } catch (err) {
          console.warn("[useRestorePendingDiagnosis] lead import", err);
        }

        // ── Upsert local guest entries (richer data than leads when present) ──
        for (const e of localEntries) {
          if (!e?.input || !e?.result) continue;
          await upsertSavedLocation(buildPayloadFromGuest(e, user.id));
          imported += 1;
        }
      } catch (e) {
        console.error("[useRestorePendingDiagnosis]", e);
      } finally {
        try { localStorage.removeItem(PENDING_DIAGNOSIS_KEY); } catch {/* */}
        try { localStorage.removeItem(GUEST_DIAGNOSIS_HISTORY_KEY); } catch {/* */}
        stripClaimParam();
        if (imported > 0 && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(SAVED_LOCATIONS_RESTORED_EVENT, {
              detail: { count: imported },
            }),
          );
        }
      }
    })();
  }, [isAuthenticated, user?.id]);
}
